import axios from "axios";

import {
  FETCH_HOMEWORKS,
  FETCH_HOMEWORK,
  SAVE_HOMEWORK,
  DELETE_HOMEWORK,
  LOADING_HOMEWORKS,
  FETCH_MY_HOMEWORKS,
  FETCH_HOMEWORK_ACCESS
} from "./type";

// Every action here rethrows: the homework screens show the server's reason
// next to the form, in the list or in their own retry dialog.
// None of them dispatches SET_ERROR, which is a full-page error with no way
// back for a student in the middle of a homework.
//
// For the same reason every request is `silentError`: the site-wide warning
// of interceptors/errorHandlingInterceptor.js would come up on top of the
// screen's own message (and reads `error`, not the `message` these endpoints
// answer with). A 401 still gets the interceptor's re-login notice.
const SILENT = { silentError: true };

// ---- teacher

// Assign a new homework
export const createHomework = (homeworkData) => async (dispatch) => {
  const response = await axios.post("homework", homeworkData, SILENT);
  const { homework } = response.data;

  dispatch({ type: SAVE_HOMEWORK, payload: { homework } });
  return homework;
};

// Fetch the homework the teacher may see, newest first
export const fetchHomeworks =
  (options = {}, callback) =>
  async (dispatch) => {
    dispatch({ type: LOADING_HOMEWORKS, payload: true });

    const { classroomId, page, limit } = options;
    const queryParams = new URLSearchParams();

    if (classroomId) queryParams.append("classroomId", classroomId);
    if (page) queryParams.append("page", page);
    if (limit) queryParams.append("limit", limit);

    const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

    try {
      const response = await axios.get(`homework${query}`, SILENT);
      const { homeworks, pagination } = response.data;

      dispatch({ type: FETCH_HOMEWORKS, payload: { homeworks, pagination } });
      dispatch({ type: LOADING_HOMEWORKS, payload: false });

      if (callback) callback(response.data);
    } catch (error) {
      console.error("Error fetching homework:", error);
      dispatch({ type: LOADING_HOMEWORKS, payload: false });
      throw error;
    }
  };

// Fetch one homework with its student roster (status, score per student)
export const fetchHomework = (homeworkId) => async (dispatch) => {
  const response = await axios.get(`homework/${homeworkId}`, SILENT);
  const { homework, roster } = response.data;

  dispatch({
    type: FETCH_HOMEWORK,
    payload: { homework: { ...homework, roster } }
  });
  return { homework, roster };
};

// Edit a homework: title, note, dueAt, status, assignees, pageIds
export const updateHomework = (homeworkId, changes) => async (dispatch) => {
  const response = await axios.patch(`homework/${homeworkId}`, changes, SILENT);
  const { homework } = response.data;

  dispatch({ type: SAVE_HOMEWORK, payload: { homework } });
  return homework;
};

export const deleteHomework = (homeworkId) => async (dispatch) => {
  await axios.delete(`homework/${homeworkId}`, SILENT);

  dispatch({ type: DELETE_HOMEWORK, payload: { homeworkId } });
};

// One student's answers: { homework, submission }
export const fetchHomeworkSubmission =
  (homeworkId, submissionId) => async () => {
    const response = await axios.get(
      `homework/${homeworkId}/submissions/${submissionId}`,
      SILENT
    );
    return response.data;
  };

// ---- student

export const fetchMyHomeworks = (callback) => async (dispatch) => {
  dispatch({ type: LOADING_HOMEWORKS, payload: true });

  try {
    const response = await axios.get("homework/mine", SILENT);
    const { homeworks } = response.data;

    dispatch({ type: FETCH_MY_HOMEWORKS, payload: { homeworks } });
    dispatch({ type: LOADING_HOMEWORKS, payload: false });

    if (callback) callback(homeworks);
  } catch (error) {
    console.error("Error fetching my homework:", error);
    dispatch({ type: LOADING_HOMEWORKS, payload: false });
    throw error;
  }
};

// The pages to answer, in order: { homework, pages }
export const fetchHomeworkPages = (homeworkId) => async () => {
  const response = await axios.get(`homework/${homeworkId}/pages`, SILENT);
  return response.data;
};

// Submit once: { submission, duplicate? }
export const submitHomework = (homeworkId, submissionData) => async () => {
  const response = await axios.post(
    `homework/${homeworkId}/submission`,
    submissionData,
    SILENT
  );
  return response.data;
};

// My result: { homework, submission }
export const fetchMyHomeworkSubmission = (homeworkId) => async () => {
  const response = await axios.get(
    `homework/${homeworkId}/my-submission`,
    SILENT
  );
  return response.data;
};

// ---- access (who may use homework; set by a super)

// What the signed-in user may do: { canAssign, canView, canAnswer }.
// A background request nobody asked for (the navbars send it on every site
// open): it must never raise a dialog, e.g. against a server that does not
// have the endpoint yet. One request at a time: both navbars and the routes
// ask on mount.
let accessRequest = null;
export const fetchHomeworkAccess = () => (dispatch) => {
  if (!accessRequest) {
    accessRequest = axios
      .get("homework/access", SILENT)
      .then((response) => {
        const { canAssign, canView, canAnswer } = response.data;
        const access = { canAssign, canView, canAnswer };
        dispatch({ type: FETCH_HOMEWORK_ACCESS, payload: { access } });
        return access;
      })
      .finally(() => {
        accessRequest = null;
      });
  }
  return accessRequest;
};

// Super: { mode: off | list | all, schools: [{id,name}], teachers: [{id,name,username}] }
export const fetchHomeworkAccessSettings = () => async () => {
  const response = await axios.get("homework/access-settings", SILENT);
  return response.data.settings;
};

export const saveHomeworkAccessSettings = (settings) => async () => {
  const response = await axios.put(
    "homework/access-settings",
    settings,
    SILENT
  );
  return response.data.settings;
};
